import React, { useState, useCallback, memo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Key, Eye, EyeOff, Lock } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const ResetPassword = memo(() => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleAuthError, showSuccess, showWarning } = useErrorHandler();
  const { login } = useAuth();

  useEffect(() => {
    const handlePasswordReset = async () => {
      // Verificar parâmetro 'code' na URL
      const code = searchParams.get('code');
      
      console.log('Reset Password - Parâmetros encontrados:', {
        code: code ? 'Presente' : 'Ausente',
        fullUrl: window.location.href
      });
      
      if (!code) {
        console.log('Código de recuperação ausente');
        showWarning('Link de recuperação inválido ou expirado.');
        navigate('/login');
        return;
      }

      try {
        // Simular validação do código (sem Supabase)
        console.log('Código de recuperação válido:', code);
        showSuccess('Link de recuperação válido! Você pode definir sua nova senha.');
      } catch (error) {
        console.error('Erro ao processar código:', error);
        showWarning('Erro ao processar link de recuperação.');
        navigate('/login');
      }
    };
    
    handlePasswordReset();
  }, [searchParams, navigate, showWarning, showSuccess]);

  const validatePassword = useCallback((password) => {
    if (password.length < 6) {
      return 'A senha deve ter pelo menos 6 caracteres.';
    }
    return null;
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Validações frontend
    if (!password || !confirmPassword) {
      showWarning('Por favor, preencha todos os campos.');
      return;
    }
    
    const passwordError = validatePassword(password);
    if (passwordError) {
      showWarning(passwordError);
      return;
    }
    
    if (password !== confirmPassword) {
      showWarning('As senhas não coincidem.');
      return;
    }

    setIsLoading(true);
    
    try {
      // Simular alteração de senha (sem Supabase)
      console.log('Simulando alteração de senha para usuário');
      
      // Delay artificial
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      showSuccess('Senha alterada com sucesso! Fazendo login...');
      
      // Simular login com nova senha
      setTimeout(async () => {
        try {
          // Usar email fictício para demonstração
          const demoEmail = 'usuario@exemplo.com';
          const loginResult = await login(demoEmail, password);
          if (loginResult.success) {
            navigate('/dashboard');
          } else {
            // Se o login falhar, redirecionar para login manual
            showSuccess('Senha alterada! Faça login com sua nova senha.');
            navigate('/login');
          }
        } catch (error) {
          console.error('Erro ao fazer login após reset:', error);
          showSuccess('Senha alterada! Faça login com sua nova senha.');
          navigate('/login');
        }
      }, 1500);
      
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      handleAuthError(error);
    } finally {
      setIsLoading(false);
    }
  }, [password, confirmPassword, validatePassword, handleAuthError, showSuccess, showWarning, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950 p-4">
      <Helmet>
        <title>Nova Senha | Media App</title>
      </Helmet>
      
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-2xl p-8 shadow-2xl shadow-cyan-500/10"
      >
        <div className="text-center mb-8">
          <div className="mx-auto h-12 w-12 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-4">
            <Key className="h-6 w-6 text-cyan-400" />
          </div>
          <h2 className="text-3xl font-bold text-white">
            Nova Senha
          </h2>
          <p className="mt-2 text-slate-400">
            Digite sua nova senha
          </p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                Nova Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-slate-500" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  className="block w-full pl-10 pr-10 py-3 border border-slate-700 rounded-lg bg-slate-950 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  placeholder="Digite sua nova senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Mínimo de 6 caracteres
              </p>
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300 mb-2">
                Confirmar Nova Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-slate-500" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  className="block w-full pl-10 pr-3 py-3 border border-slate-700 rounded-lg bg-slate-950 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  placeholder="Confirme sua nova senha"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  minLength={6}
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-cyan-500/20"
          >
            {isLoading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Alterando...
              </span>
            ) : (
              'Alterar Senha'
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
});

ResetPassword.displayName = 'ResetPassword';

export default ResetPassword;